// Chart.js chart types: 'line' | 'bar' | 'radar' | 'pie' | 'doughtnut' | 'polarArea' | 'bubble' | 'scatter';
export enum GalaxyChartType {
  LINE = 'line',
  BAR = 'bar',
}

export type GalaxyChartAvailableTypes = GalaxyChartType.LINE | GalaxyChartType.BAR;
export type GalaxyPointTypes =
  | 'circle'
  | 'cross'
  | 'crossRot'
  | 'dash'
  | 'line'
  | 'rect'
  | 'rectRounded'
  | 'rectRot'
  | 'star'
  | 'triangle';

/**
 * An example of using a custom tick for showing USD currency
 */
export function USDCurrencyTick(value) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    notation: 'compact',
    compactDisplay: 'short',
  } as any).format(value);
}

/**
 * Galaxy gradient fill color. It will order indexes 0 -> ... top -> bottom
 */
export interface GalaxyGradientFill {
  colors: string[];
}
export type GalaxyChartColor = string | GalaxyGradientFill;

/**
 * Configure Chart.js Legend
 *   For more information:
 *   https://www.chartjs.org/docs/latest/configuration/legend.html
 */
export interface GalaxyChartLegendConfig {
  display?: boolean;
  position?: 'top' | 'left' | 'right' | 'bottom';
  align?: 'start' | 'center' | 'end';

  onClick?: (event, legendItem) => void;
  onHover?: (event, legendItem) => void;
  onLeave?: (event, legendItem) => void;
}

/**
 * Configure the Chart.js title
 *   For more information:
 *   https://www.chartjs.org/docs/latest/configuration/title.html
 */
export interface GalaxyChartTitle {
  /// Show the title, default false
  display?: boolean;
  // Position of the title, default top
  position?: 'top' | 'left' | 'right' | 'bottom';
  // Font Settings
  fontSize?: number;
  fontFamily?: string;
  fontColor?: GalaxyChartColor;
  fontStyle?: string;
  // Number of pixels to add above and below the title text
  padding?: number;
  // Height of an individual line
  lineHeight?: number | string;
  // Title text to display.
  text: string;
}

/**
 * Layout config for the chart
 *   For more information:
 *   https://www.chartjs.org/docs/latest/configuration/layout.html
 */
export interface GalaxyChartLayout {
  // If this value is a number, it is applied to all sides of the chart (left, top, right, bottom).
  // If this value is an object, the left property defines the left padding.
  // Similarly the right, top and bottom properties can also be specified.
  padding: number | Record<string, unknown>;
}

/**
 * Configure Chart.js Line Chart
 *   This is only a subset of options available on the config.
 *   Just to keep things streamlined and simple. If there is a missing config
 *   that you could utillize, you can add it to the config.
 *   For more information:
 *   https://www.chartjs.org/docs/latest/charts/line.html#line-styling
 */
export interface GalaxyLineChartConfig {
  /**
   * GENERAL SETTINGS
   */
  // Bezier curve tension of the line. Set to 0 to draw straightlines.
  lineTension?: number;
  // Bar chart style steps in a line chart
  //   https://www.chartjs.org/docs/latest/charts/line.html#stepped-line
  steppedLine?: boolean | 'before' | 'after' | 'middle';

  /**
   * LINE STYLING
   */
  // How to fill the area under the line
  //  https://www.chartjs.org/docs/latest/charts/area.html
  //  false = disabled, enabled by default
  fill?: any;
  // The line fill color
  backgroundColor?: GalaxyChartColor;
  // The line color
  borderColor?: GalaxyChartColor;
  // The line width (in pixels).
  borderWidth?: number;

  /**
   * POINT STYLING
   */
  // The fill color for points.
  pointBackgroundColor?: GalaxyChartColor;
  // The border color for points.
  pointBorderColor?: GalaxyChartColor;
  // The width of the point border (in pixels).
  pointBorderWidth?: number;
  // The radius of the point shape. If set to 0, the point is not rendered.
  pointRadius?: number;
  // The style of the point
  pointStyle?: GalaxyPointTypes;
  // The pixel size of the non-displayed point that reacts to mouse events.
  pointHitRadius?: number;
}

export interface GalaxyBarChartConfig {
  backgroundColor: GalaxyChartColor[];
}
